import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import DynamicTable from 'Components/DynamicTable/DynamicTable'
import CustomerDisplayFilters from './components/CustomerDisplayFilters'
import CustomerDisplayPreviewModal from './components/CustomerDisplayPreviewModal'
import useColumnSearchCustomerDisplay from './hooks/useColumnSearchCustomerDisplay'
import DeleteCustomerDisplayModal from './components/DeleteCustomerDisplayModal/DeleteCustomerDisplayModal'
import {
  CUSTOMER_DISPLAY_PAGE_SIZE_OPTIONS,
  CUSTOMER_DISPLAY_ROUTES,
} from './constants/customerDisplay.constants'
import {
  buildCustomerDisplaySearchPayload,
  mapCustomerDisplayUpdatePayload,
} from './utils/customerDisplay.utils'
import './SearchCustomerDisplay.css'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import MiniLoading from 'Components/MiniLoading'
import {
  removeCustomerDisplaySetting,
  fetchCustomerDisplaySettings,
  modifyCustomerDisplaySetting,
} from 'redux/actions/customerDisplayActions'
import { fetchCompanies } from 'redux/actions/companyAction'
import { fetchListWharehouse } from 'redux/actions/clientsActions'
import { useDispatch } from 'react-redux'
import CustomSnackbar from 'Components/CustomSnackbar'

const WINDOW_SIZE = 3

const getPageDisplayOrderFallback = ({ currentPage, pageSize }, index) => {
  const page = Math.max(Number(currentPage) || 1, 1)
  const size = Math.max(Number(pageSize) || 0, 0)

  return (page - 1) * size + index + 1
}

const getDisplayOrderSlots = (currentItems, pagination) => {
  return currentItems.map((item, index) => {
    const displayOrder = Number(item.DisplayOrder)
    const hasDisplayOrder =
      item.DisplayOrder !== null &&
      item.DisplayOrder !== undefined &&
      item.DisplayOrder !== ''

    return hasDisplayOrder && Number.isFinite(displayOrder)
      ? displayOrder
      : getPageDisplayOrderFallback(pagination, index)
  })
}

const SearchCustomerDisplay = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [items, setItems] = useState([])
  const [companies, setCompanies] = useState([])
  const [branchOffices, setBranchOffices] = useState([])

  const [selectedCompany, setSelectedCompany] = useState('')
  const [selectedBranchOffice, setSelectedBranchOffice] = useState('')
  const [selectedMediaType, setSelectedMediaType] = useState('')

  const [loadingData, setLoadingData] = useState(false)
  const [previewItem, setPreviewItem] = useState(null)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showModalDeleteCustomerDisplay, setShowModalDeleteCustomerDisplay] =
    useState(false)
  const [pageNumbers, setPageNumbers] = useState([])
  const [pageWindowFirst, setPageWindowFirst] = useState(0)
  const [pageWindowLast, setPageWindowLast] = useState(WINDOW_SIZE)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  })
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const resetPageWindow = () => {
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
  }

  const searchSettings = useCallback(async () => {
    setLoadingData(true)

    try {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)
      startDate.setHours(0, 0, 0, 0)

      const endDate = new Date()
      endDate.setMonth(endDate.getMonth() + 6)
      endDate.setHours(23, 59, 59, 999)
      const payload = buildCustomerDisplaySearchPayload({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        pagination,
        selectedCompany,
        selectedBranchOffice,
        selectedMediaType,
      })

      const data = await dispatch(fetchCustomerDisplaySettings(payload))
      const sortedData = data.sort((a, b) => {
        const aOrder = a.DisplayOrder ?? Number.MAX_SAFE_INTEGER
        const bOrder = b.DisplayOrder ?? Number.MAX_SAFE_INTEGER
        return aOrder - bOrder
      })

      setItems(sortedData)

      setPagination(prev => ({
        ...prev,
        totalRecords: sortedData[0]?.TotalRecords || sortedData.length,
        totalPages: sortedData[0]?.TotalPages || 1,
      }))
      setPageNumbers(
        Array.from({ length: sortedData[0]?.TotalPages || 1 }, (_, i) => i + 1)
      )
    } catch (error) {
      setItems([])
      setPagination(prev => ({
        ...prev,
        totalRecords: 0,
        totalPages: 1,
      }))
      setPageNumbers([])
    } finally {
      setLoadingData(false)
    }
  }, [
    pagination.currentPage,
    pagination.pageSize,
    selectedCompany,
    selectedBranchOffice,
    selectedMediaType,
  ])

  const searchCompanies = async () => {
    try {
      const data = await dispatch(fetchCompanies())
      setCompanies(data)
    } catch (error) {
      setCompanies([])
    }
  }

  useEffect(() => {
    searchSettings().then()
  }, [searchSettings])

  useEffect(() => {
    searchCompanies().then()
  }, [])

  const handlePrevPage = () => {
    if (pagination.currentPage <= 1) return
    setPageWindowFirst(prev => Math.max(0, prev - 1))
    setPageWindowLast(prev => Math.max(WINDOW_SIZE, prev - 1))
    setPagination(prev => ({
      ...prev,
      currentPage: prev.currentPage - 1,
    }))
  }

  const handleNextPage = () => {
    if (pagination.currentPage >= pagination.totalPages) return
    setPageWindowFirst(prev =>
      Math.min(pageNumbers.length - WINDOW_SIZE, prev + 1)
    )
    setPageWindowLast(prev => Math.min(pageNumbers.length, prev + 1))
    setPagination(prev => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }))
  }

  const handlePageClick = e => {
    const page = parseInt(e.target.value)
    setPageWindowFirst(page - 1)
    setPageWindowLast(page - 1 + WINDOW_SIZE)
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }))
  }

  const handleReturnFirst = () => {
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }))
  }

  const handleJumpForward = () => {
    const newFirst = pageWindowLast
    const newLast = Math.min(pageNumbers.length, pageWindowLast + WINDOW_SIZE)
    const newPage = pageWindowLast + 1
    setPageWindowFirst(newFirst)
    setPageWindowLast(newLast)
    setPagination(prev => ({
      ...prev,
      currentPage: newPage,
    }))
  }

  const handleClose = () => {
    setSnackbarMessage(prev => ({
      ...prev,
      open: false,
    }))
  }

  const handleCompanyChange = async companyId => {
    setSelectedCompany(companyId)
    setSelectedBranchOffice('')
    resetPageWindow()
    setPagination(prev => ({ ...prev, currentPage: 1 }))

    if (!companyId) {
      setBranchOffices([])
      return
    }

    try {
      const payload = {
        CompanyId: companyId,
        PageSize: 100,
        PageNumber: 1,
      }
      const branchesInfo = await dispatch(fetchListWharehouse(payload))
      const branches = branchesInfo
        .map(branch => {
          return { ...branch.Warehouses[0] }
        })
        .filter(warehouse => {
          return warehouse.Description !== undefined
        })
      setBranchOffices(branches)
    } catch (error) {
      setBranchOffices([])
    }
  }

  const handleClearFilters = () => {
    setSelectedCompany('')
    setSelectedBranchOffice('')
    setSelectedMediaType('')
    setBranchOffices([])
    resetPageWindow()
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const handlePageSizeChange = event => {
    resetPageWindow()
    setPagination(prev => ({
      ...prev,
      pageSize: Number(event.target.value),
      currentPage: 1,
    }))
  }

  const handleToggleStatus = async item => {
    const payload = mapCustomerDisplayUpdatePayload(item, !item.Status)

    const response = await dispatch(modifyCustomerDisplaySetting(payload))

    if (response) {
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.STATUS_UPDATED'),
        severity: 'success',
      })
      setItems(prev =>
        prev.map(row =>
          row.CustomerDisplaySettingId === item.CustomerDisplaySettingId
            ? { ...row, Status: !row.Status }
            : row
        )
      )
    } else {
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.STATUS_UPDATE_ERROR'),
        severity: 'error',
      })
    }
  }

  const handleDelete = item => {
    setDeleteItem(item)
    setShowModalDeleteCustomerDisplay(true)
  }

  const handleConfirmDeleteCustomerDisplay = async () => {
    if (!deleteItem) return

    const payload = mapCustomerDisplayUpdatePayload(deleteItem, false)

    const response = await dispatch(removeCustomerDisplaySetting(payload))

    if (response) {
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.STATUS_UPDATED'),
        severity: 'success',
      })
      setDeleteItem(null)
      searchSettings().then()
    } else {
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.STATUS_UPDATE_ERROR'),
        severity: 'error',
      })
    }
    setShowModalDeleteCustomerDisplay(false)
  }

  const handleRowsReorder = async reorderedItems => {
    const previousItems = [...items]
    const displayOrderSlots = getDisplayOrderSlots(previousItems, pagination)

    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      DisplayOrder: displayOrderSlots[index],
    }))

    setItems(updatedItems)

    const payload = updatedItems.map(item => ({
      CustomerDisplaySettingId: item.CustomerDisplaySettingId,
      Name: item.Name,
      MediaType: item.MediaType,
      FileUrl: item.FileUrl,
      DisplayOrder: item.DisplayOrder,
      StartDate: item.StartDate,
      EndDate: item.EndDate,
      BranchOfficeId: item.BranchOfficeId,
      CompanyId: item.CompanyId,
      Status: item.Status,
    }))

    const response = await dispatch(modifyCustomerDisplaySetting(payload))

    if (response) {
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.ORDER_UPDATED'),
        severity: 'success',
      })
    } else {
      setItems(previousItems)
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.ORDER_UPDATE_ERROR'),
        severity: 'error',
      })
    }
  }

  const columns = useColumnSearchCustomerDisplay({
    onPreview: setPreviewItem,
    onEdit: item =>
      history.push(CUSTOMER_DISPLAY_ROUTES.EDIT(item.CustomerDisplaySettingId)),
    onDelete: handleDelete,
    onToggleStatus: handleToggleStatus,
  })

  return (
    <>
      <div
        className="search-customer-display"
        data-testid="container-customer-display">
        <h4
          className="search-customer-display-title"
          data-testid="title-customer-display">
          {t('CUSTOMER_DISPLAY_SETTINGS.LIST.TITLE_FILTER')}
        </h4>

        <CustomerDisplayFilters
          companies={companies}
          branchOffices={branchOffices}
          selectedCompany={selectedCompany}
          selectedBranchOffice={selectedBranchOffice}
          selectedMediaType={selectedMediaType}
          loading={loadingData}
          onCompanyChange={handleCompanyChange}
          onBranchOfficeChange={value => {
            setSelectedBranchOffice(value)
            resetPageWindow()
            setPagination(prev => ({ ...prev, currentPage: 1 }))
          }}
          onMediaTypeChange={value => {
            setSelectedMediaType(value)
            resetPageWindow()
            setPagination(prev => ({ ...prev, currentPage: 1 }))
          }}
          onClearFilters={handleClearFilters}
          onCreate={() => history.push(CUSTOMER_DISPLAY_ROUTES.CREATE)}
        />
        {loadingData ? (
          <MiniLoading />
        ) : (
          <DynamicTable
            columns={columns}
            data={items}
            loadingData={loadingData}
            enableRowDrag
            getRowId={row => row.CustomerDisplaySettingId}
            onRowsReorder={handleRowsReorder}
          />
        )}
        <div
          className="search-customer-display-footer"
          data-testid="container-pagination">
          <div className="search-footer">
            <div className="footer-content">
              <p>
                {pagination.totalRecords > 0
                  ? t(
                      'CUSTOMER_DISPLAY_SETTINGS.LIST.SHOWING_PAGE_OF_TOTAL_ENTRIES',
                      {
                        startEntry:
                          pagination.totalRecords > 0
                            ? (pagination.currentPage - 1) *
                                pagination.pageSize +
                              1
                            : 0,
                        endEntry: Math.min(
                          pagination.currentPage * pagination.pageSize,
                          pagination.totalRecords
                        ),
                        totalRecords: pagination.totalRecords,
                      }
                    )
                  : t('CUSTOMER_DISPLAY_SETTINGS.LIST.NO_RESULTS')}
              </p>

              <select
                data-testid="input-page-size"
                name="inputPageSize"
                className="page-size-selector"
                onChange={handlePageSizeChange}
                value={pagination.pageSize}>
                {CUSTOMER_DISPLAY_PAGE_SIZE_OPTIONS.map(size => (
                  <option
                    key={size}
                    value={size}
                    data-testid={`option-page-size-${size}`}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="customer-display-pagination">
            <button
              className="btn-prev-page"
              data-testid="btn-prev-page"
              disabled={pagination.currentPage <= 1 || loadingData}
              onClick={handlePrevPage}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                style={{ fontSize: '1.8rem' }}
              />
            </button>

            {pageWindowFirst > 0 && (
              <button className="btn-regular-page" onClick={handleReturnFirst}>
                ...
              </button>
            )}

            {pageNumbers.slice(pageWindowFirst, pageWindowLast).map(pageNum => (
              <button
                data-testid={`btn-page-${pageNum}`}
                key={pageNum}
                className={
                  pagination.currentPage === pageNum
                    ? 'btn-active-page'
                    : 'btn-regular-page'
                }
                value={pageNum}
                onClick={handlePageClick}
                disabled={loadingData}>
                {pageNum}
              </button>
            ))}

            {pageWindowLast < pageNumbers.length && (
              <button className="btn-regular-page" onClick={handleJumpForward}>
                ...
              </button>
            )}

            <button
              className="btn-next-page"
              data-testid="btn-next-page"
              disabled={
                pagination.currentPage >= pagination.totalPages || loadingData
              }
              onClick={handleNextPage}>
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: '1.8rem' }}
              />
            </button>
          </div>
        </div>

        <CustomerDisplayPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
        <DeleteCustomerDisplayModal
          showModalDeleteCustomerDisplay={showModalDeleteCustomerDisplay}
          setShowModalDeleteCustomerDisplay={setShowModalDeleteCustomerDisplay}
          handleConfirmDeleteCustomerDisplay={
            handleConfirmDeleteCustomerDisplay
          }
        />
      </div>
      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleClose}
      />
    </>
  )
}

export default SearchCustomerDisplay
